/**
 * ObjectiveChatInput - chat-style input used as the primary affordance on the
 * wizard's objectives step. Patterned after Claude / ChatGPT / Gemini: a flat
 * dark surface with a subtle border and the bare-minimum action chrome.
 *
 *   - Multi-line auto-growing textarea. Enter sends, Shift+Enter inserts a
 *     newline by default. Respondent expanded answers use Enter for paragraphs
 *     and a labelled send action.
 *   - Mic button (right) - only right-side action. Toggles browser dictation
 *     via Web Speech API; hides itself when the API isn't available so we
 *     don't expose a non-functional control on Firefox/Safari.
 *   - Paperclip button (left) - accepts `.txt`, `.md`, `.markdown`, `.pdf`.
 *     Extracted text rides along on submit (see `extractTextFromFile`).
 *   - `leftActions` slot (left, before the paperclip) - used by the wizard
 *     to mount the workspace dropdown as a compact chip on the composer.
 *
 * Submission semantics: the parent hands in `value`/`onChange` for the typed
 * text. On send, the component emits `onSubmit(text, attachment)` where the
 * attachment may be null. Empty submissions are dropped silently.
 *
 * @module components/ObjectiveChatInput
 */

import { ButtonContent } from './ui/ButtonContent';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  ACCEPTED_FILE_TYPES,
  buildInlineAttachment,
  type InlineAttachment,
} from '../lib/file-attachment';
import { PhoneNumberInput } from './ui/PhoneNumberInput';
import { useSpeechDictation } from '../lib/useSpeechDictation';
import { captureException } from '../lib/telemetry/posthog/frontend';

/** Props accepted by `ObjectiveChatInput`. */
export interface ObjectiveChatInputProps {
  /** Report whether an allowed file is queued or being prepared, without exposing its data. */
  onAttachmentChange?: (hasAttachment: boolean) => void;
  /** Controlled typed-text value. */
  value: string;
  /** Fires on every textarea keystroke. */
  onChange: (value: string) => void;
  /**
   * Fires when the user hits send. Caller threads `attachment` (if any)
   * straight to Gemini as an `inlineData` part on the worker side; `text`
   * is the typed prompt verbatim. At least one of the two will be non-empty.
   */
  onSubmit: (text: string, attachment: InlineAttachment | null) => void;
  /** Gate file selection and submission, privately retaining queued files while unavailable. */
  allowAttachments?: boolean;
  /** Disables the complete composer. */
  disabled?: boolean;
  /** Show the shared loading dots inside the send button and disable submission. */
  loading?: boolean;
  /** Blocks submission while leaving typing, dictation, and attachment available. */
  submitDisabled?: boolean;
  /** Placeholder shown when the textarea is empty. */
  placeholder?: string;
  /** When true, renders a more compact variant for in-page slots. */
  compact?: boolean;
  /** Respondent answers can sit directly beside their question without a filled chat card. */
  appearance?: 'card' | 'inline';
  /**
   * Optional slot rendered at the START of the action row (before the
   * paperclip / mic buttons). Used by the wizard to mount the workspace
   * dropdown so the "drafting for" affordance lives on the chat modal
   * itself rather than as a separate banner above it.
   */
  leftActions?: React.ReactNode;
  /** Accessible name for the send action. */
  sendLabel?: string;
  /** Keep refinement secondary when its containing document has a primary Save action. */
  sendAppearance?: 'primary' | 'subtle';
  /** Keeps a readable action visible, including before the first answer. */
  showSendLabel?: boolean;
  /** Accessible name independent of the authored placeholder. */
  inputLabel?: string;
  /** IDs of authored instructions associated with this input. */
  describedBy?: string;
  /** Marks a rejected answer while keeping its error description accessible. */
  invalid?: boolean;
  /** Initial answer height, before content-driven growth. */
  rows?: number;
  /** Use a native single-line input that scrolls horizontally instead of growing. */
  singleLine?: boolean;
  /** Expanded answers reserve Enter for paragraphs; modifier Enter still sends. */
  submitOnEnter?: boolean;
  /** Input mode used by the active authored question. */
  inputMode?: React.HTMLAttributes<HTMLTextAreaElement>['inputMode'];
  /** Browser autofill hint for the active authored question; email input mode implies `email`. */
  autoComplete?: string;
  /** Maximum auto-grown textarea height in pixels. */
  maxHeight?: number;
}

/** Format a byte count for the file pill (e.g. "12 KB", "2.4 MB"). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PaperclipIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.6}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
    />
  </svg>
);

const MicIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.6}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
    />
  </svg>
);

const FileIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.6}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m4.5 11.25v6m3-3l-3 3-3-3M6.75 21h10.5A2.25 2.25 0 0019.5 18.75V9.75a2.25 2.25 0 00-2.25-2.25H8.25A2.25 2.25 0 006 9.75v9A2.25 2.25 0 008.25 21z"
    />
  </svg>
);

const SendIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l14-7-7 14-2-5-5-2z" />
  </svg>
);

export const ObjectiveChatInput: React.FC<ObjectiveChatInputProps> = ({
  onAttachmentChange,
  value,
  onChange,
  onSubmit,
  allowAttachments = true,
  disabled: disabledProp = false,
  loading = false,
  submitDisabled = false,
  placeholder = 'Ask holito',
  compact = false,
  appearance = 'card',
  leftActions,
  sendLabel = 'Send',
  sendAppearance = 'primary',
  showSendLabel = false,
  inputLabel,
  describedBy,
  invalid,
  rows,
  singleLine = false,
  submitOnEnter = true,
  inputMode = 'text',
  autoComplete,
  maxHeight,
}) => {
  const disabled = disabledProp || loading;
  const textareaRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const composing = useRef(false);
  const restoreInputFocus = useRef(false);
  /** Preserve editing focus when the authored display replaces input with textarea. */
  const setInputRef = useCallback((element: HTMLTextAreaElement | HTMLInputElement | null) => {
    if (!element) {
      restoreInputFocus.current = textareaRef.current === document.activeElement;
      composing.current = false;
    }
    textareaRef.current = element;
    if (element && restoreInputFocus.current) {
      restoreInputFocus.current = false;
      element.focus({ preventScroll: true });
    }
  }, []);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Attached file state ──
  // We hold the raw `File` purely for rendering (name + display size) and the
  // base64-encoded `attachment` for the worker. Encoding happens on attach so
  // submit is instant; if encoding fails the user sees the error inline and
  // the attachment is skipped.
  const [attachment, setAttachment] = useState<InlineAttachment | null>(null);
  const [encoding, setEncoding] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const availableAttachment = allowAttachments ? attachment : null;
  const preparingAttachment = allowAttachments && encoding;
  const hasQueuedAttachment = availableAttachment !== null || preparingAttachment;

  useEffect(() => { onAttachmentChange?.(hasQueuedAttachment); }, [hasQueuedAttachment, onAttachmentChange]);

  // ── Voice dictation ──
  // Mic toggles browser dictation, appending the transcript to the typed value.
  // Hidden when the Web Speech API is unavailable (Firefox/Safari) so we don't
  // expose a dead control. Disabled while a file is encoding or the parent is busy.
  const {
    supported: speechSupported,
    recording,
    toggle: toggleRecording,
  } = useSpeechDictation(value, onChange, 'ObjectiveChatInput.dictation', disabled || preparingAttachment);

  // Auto-grow on both text and container-width changes. The width observer is
  // required when this same composer opens inside the animated call panel.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || singleLine) return undefined;
    const resize = () => {
      if (el.clientWidth === 0) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, maxHeight ?? (compact ? 140 : 220))}px`;
    };
    let width = el.clientWidth;
    let frame: number | null = null;
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => { frame = null; resize(); });
    });
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [value, compact, maxHeight, rows, singleLine]);

  const handleSubmit = useCallback(() => {
    if (disabled || submitDisabled) return;
    if (preparingAttachment) return;
    const trimmed = value.trim();
    if (!trimmed && !availableAttachment) return;
    onSubmit(trimmed, availableAttachment);
    if (allowAttachments) {
      setAttachment(null);
      setFileError(null);
    }
  }, [disabled, submitDisabled, preparingAttachment, value, availableAttachment, allowAttachments, onSubmit]);

  const openFilePicker = useCallback(() => {
    if (!allowAttachments || disabled || encoding) return;
    fileInputRef.current?.click();
  }, [allowAttachments, disabled, encoding]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset so picking the same file twice still triggers a change event.
      if (e.target) e.target.value = '';
      if (!allowAttachments || !file) return;

      setFileError(null);
      setEncoding(true);
      try {
        const next = await buildInlineAttachment(file);
        setAttachment(next);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Could not read file.';
        setFileError(message);
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'ObjectiveChatInput.buildInlineAttachment',
          { fileName: file.name, fileType: file.type, fileSize: file.size },
        );
      } finally {
        setEncoding(false);
      }
    },
    [allowAttachments],
  );

  const removeAttachedFile = useCallback(() => {
    setAttachment(null);
    setFileError(null);
  }, []);

  // Authoring keeps its compact icon action. Respondent answering uses a
  // stable labelled action so sending is discoverable before typing.
  const hasContent = value.trim().length > 0 || availableAttachment !== null;
  const canSend = !disabled && !submitDisabled && !preparingAttachment && hasContent;
  const inline = appearance === 'inline';
  const InputElement = singleLine ? 'input' : 'textarea';
  const inputClassName = `${inline ? `${(rows ?? 1) > 1 || !submitOnEnter ? 'w-full basis-full' : 'min-w-0 flex-1'} px-2 py-2` : 'w-full px-4 pt-3.5 pb-1'} resize-none bg-transparent text-base leading-6 text-foreground placeholder:text-muted-foreground focus:outline-none scrollbar-dark`;

  return (
    <div
      className={`${inline ? 'flex flex-wrap items-end gap-1 rounded-lg border border-border bg-transparent p-1' : 'glass-card overflow-hidden'} focus-within:outline-1 focus-within:-outline-offset-1 focus-within:outline-ring`}
      data-testid="objective-chat-input"
    >
      {/* Attached-file pill row - only renders when a file is attached. */}
      {availableAttachment && (
        <div className={`flex items-center gap-2 ${inline ? 'w-full px-2 pt-1' : 'px-4 pt-3'}`}>
          <span
            data-testid="objective-chat-file-pill"
            className="inline-flex max-w-full items-center gap-2 rounded-lg bg-surface border border-border pl-2 pr-1 py-1 text-xs text-muted-foreground"
          >
            <FileIcon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium">{availableAttachment.fileName}</span>
            <span className="text-muted-foreground shrink-0">
              {formatBytes(availableAttachment.byteLength)}
            </span>
            <button
              type="button"
              aria-label="Remove attached file"
              onClick={removeAttachedFile}
              className="ml-1 inline-flex w-5 h-5 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover"
            >
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.4}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </span>
        </div>
      )}

      {/* Textarea - Enter sends, Shift+Enter newline. */}
      {inputMode === 'tel' ? <PhoneNumberInput appearance="composer" inputRef={setInputRef} value={value} onChange={onChange}
        onSubmit={handleSubmit} disabled={disabled || preparingAttachment} placeholder={placeholder}
        inputLabel={inputLabel ?? placeholder} describedBy={describedBy} invalid={invalid} inputClassName={inputClassName} /> : <InputElement
        ref={setInputRef}
        data-testid="objective-chat-textarea"
        value={value}
        aria-label={inputLabel ?? placeholder}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        inputMode={inputMode}
        autoComplete={autoComplete ?? (inputMode === 'email' ? 'email' : undefined)}
        autoCapitalize={inputMode === 'email' ? 'none' : undefined}
        spellCheck={inputMode === 'email' ? false : undefined}
        onChange={(e) => onChange(e.target.value)}
        placeholder={preparingAttachment ? 'Reading your file…' : placeholder}
        disabled={disabled || preparingAttachment}
        rows={singleLine ? undefined : rows ?? (compact ? 2 : 3)}
        className={inputClassName}
        onCompositionStart={() => { composing.current = true; }}
        onCompositionEnd={() => { composing.current = false; }}
        onKeyDown={(e) => {
          if (composing.current || e.nativeEvent.isComposing || e.keyCode === 229) return;
          if (e.key === 'Enter' && !e.shiftKey && (submitOnEnter || e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />}

      {/* File extraction error - surfaced so failed uploads aren't silent. */}
      {allowAttachments && fileError && (
        <p
          data-testid="objective-chat-file-error"
          className={`${inline ? 'w-full px-2' : 'px-4'} pb-1 text-xs text-danger`}
          role="alert"
        >
          {fileError}
        </p>
      )}

      {/* Action row: workspace slot + paperclip on the left, mic on the right. */}
      <div className={`flex items-center gap-1 ${inline ? 'shrink-0 pb-0.5' : 'px-2.5 pb-2 pt-1'}`}>
        {leftActions && (
          <div
            data-testid="objective-chat-left-actions"
            className="flex items-center"
          >
            {leftActions}
          </div>
        )}
        {allowAttachments && <>
        <input
          ref={fileInputRef}
          type="file"
          data-testid="objective-chat-file-input"
          accept={ACCEPTED_FILE_TYPES}
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          type="button"
          data-testid="objective-chat-attach"
          aria-label="Attach a file"
          onClick={openFilePicker}
          disabled={disabled || encoding}
          className="cursor-pointer focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-ring inline-flex w-8 h-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <PaperclipIcon className="w-4 h-4" />
        </button>
        </>}

        {/* Right-aligned action group: mic (when supported) + send. The send
            button transitions its own width so the right group expands
            smoothly the moment the textarea has content. */}
        <div className="ml-auto flex items-center">
          {speechSupported && (
            <button
              type="button"
              data-testid="objective-chat-mic"
              aria-label={recording ? 'Stop recording' : 'Speak'}
              aria-pressed={recording}
              onClick={toggleRecording}
              disabled={disabled || preparingAttachment}
              className={`cursor-pointer focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-ring inline-flex w-8 h-8 items-center justify-center rounded-full transition-colors ${
                recording
                  ? 'bg-danger/20 text-danger animate-pulse'
                  : 'text-muted-foreground hover:text-foreground hover:bg-surface-hover'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <MicIcon className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            data-testid="objective-chat-send"
            aria-label={sendLabel}
            aria-busy={loading}
            aria-hidden={!showSendLabel && !hasContent}
            tabIndex={showSendLabel || hasContent ? 0 : -1}
            onClick={handleSubmit}
            disabled={!canSend}
            className={`cursor-pointer overflow-hidden inline-flex items-center justify-center gap-2 ${sendAppearance === 'primary' ? 'button-accent' : 'text-foreground hover:bg-surface-raised-hover'} focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-ring disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 ease-out motion-reduce:transition-none ${
              showSendLabel ? 'min-h-11 rounded-lg px-4 ml-1 text-sm' : hasContent ? 'h-8 rounded-full w-8 ml-1 opacity-100' : 'h-8 rounded-full w-0 ml-0 opacity-0 pointer-events-none'
            }`}
          >
            <ButtonContent loading={loading}>{showSendLabel ? <span>{sendLabel}</span> : <SendIcon className="w-4 h-4 shrink-0" />}</ButtonContent>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObjectiveChatInput;
